import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaPlane, 
  FaPassport, 
  FaDirections, 
  FaBus, 
  FaTrain, 
  FaTaxi, 
  FaSubway, 
  FaCar, 
  FaMapMarkerAlt, 
  FaClock, 
  FaMoneyBillWave, 
  FaLanguage, 
  FaExclamationTriangle, 
  FaCheckCircle, 
  FaInfoCircle,
  FaArrowRight,
  FaDownload,
  FaPhone,
  FaGlobe,
  FaCalendarAlt,
  FaHandshake,
  FaInstagram,
  FaTelegram,
  FaWhatsapp
} from 'react-icons/fa';

const TravelGuide: React.FC = () => {
  const [activeSection, setActiveSection] = useState('airports');

  const sections = [
    { id: 'airports', label: 'Airports & Arrival', icon: <FaPlane /> },
    { id: 'transportation', label: 'Transportation', icon: <FaBus /> },
    { id: 'phrases', label: 'Essential Phrases', icon: <FaLanguage /> },
    { id: 'tips', label: 'Travel Tips', icon: <FaInfoCircle /> },
    { id: 'resources', label: 'Resources', icon: <FaGlobe /> }
  ];

  const airportInfo = [
    {
      name: 'Imam Khomeini International Airport (IKA)',
      location: 'Tehran',
      code: 'IKA',
      features: ['International flights', 'Visa on arrival', 'Currency exchange', 'Transport to city'],
      tips: 'Located 30km southwest of Tehran. Use official airport taxis or metro to reach the city.'
    },
    {
      name: 'Mehrabad International Airport (THR)',
      location: 'Tehran',
      code: 'THR',
      features: ['Domestic flights', 'Some regional flights', 'City center location'],
      tips: 'Located in western Tehran, easier access to city center but fewer international connections.'
    },
    {
      name: 'Shiraz International Airport (SYZ)',
      location: 'Shiraz',
      code: 'SYZ',
      features: ['International connections', 'Historical city access', 'Modern terminal'],
      tips: 'Gateway to southern Iran and historical sites like Persepolis.'
    }
  ];

  const transportationOptions = [
    {
      type: 'Metro/Subway',
      icon: <FaSubway className="text-blue-600" />,
      description: 'Fast, cheap, and reliable in major cities',
      cities: ['Tehran', 'Mashhad', 'Shiraz', 'Isfahan'],
      tips: 'Buy rechargeable cards. Women-only cars available during rush hours.',
      phrases: ['ایستگاه مترو کجاست؟', 'Where is the metro station?']
    },
    {
      type: 'Bus',
      icon: <FaBus className="text-green-600" />,
      description: 'Extensive network covering most areas',
      cities: 'All major cities',
      tips: 'Use Snapp app for real-time tracking. Pay with cash or card.',
      phrases: ['این اتوبوس به کجا می‌رود؟', 'Where does this bus go?']
    },
    {
      type: 'Taxi',
      icon: <FaTaxi className="text-yellow-600" />,
      description: 'Convenient but negotiate price first',
      cities: 'Everywhere',
      tips: 'Use Snapp app for ride-hailing. Traditional taxis need price negotiation.',
      phrases: ['تاکسی بگیرید', 'Take a taxi']
    },
    {
      type: 'Train',
      icon: <FaTrain className="text-purple-600" />,
      description: 'Comfortable long-distance travel',
      cities: 'Major cities connected',
      tips: 'Book tickets in advance. Overnight trains available to major destinations.',
      phrases: ['قطار ساعت هشت حرکت می‌کند', 'The train leaves at 8']
    }
  ];

  const essentialPhrases = [
    {
      persian: 'سلام',
      transliteration: 'Salam',
      english: 'Hello',
      context: 'General greeting'
    },
    {
      persian: 'چطور می‌روم به...؟',
      transliteration: 'Chetor miram be...?',
      english: 'How do I go to...?',
      context: 'Asking for directions'
    },
    {
      persian: 'کجاست؟',
      transliteration: 'Kojast?',
      english: 'Where is it?',
      context: 'Finding locations'
    },
    {
      persian: 'بلیط چقدر است؟',
      transliteration: 'Belit cheghadr ast?',
      english: 'How much is the ticket?',
      context: 'Transportation costs'
    },
    {
      persian: 'چند دقیقه طول می‌کشد؟',
      transliteration: 'Chand daghighe tool mikeshad?',
      english: 'How many minutes does it take?',
      context: 'Time estimation'
    },
    {
      persian: 'متوجه نمی‌شوم',
      transliteration: 'Motavajjeh nemishavam',
      english: 'I don\'t understand',
      context: 'Communication difficulties'
    },
    {
      persian: 'لطفاً آهسته صحبت کنید',
      transliteration: 'Lotfan aheste sohbat konid',
      english: 'Please speak slowly',
      context: 'Asking for slower speech'
    },
    {
      persian: 'ممنون',
      transliteration: 'Mamnoon',
      english: 'Thank you',
      context: 'Expressing gratitude'
    }
  ];

  const travelTips = [
    {
      category: 'Before You Go',
      tips: [
        'Check visa requirements (many nationalities can get visa on arrival)',
        'Download offline maps and translation apps',
        'Learn basic Persian phrases',
        'Research cultural customs and dress codes',
        'Check current travel advisories'
      ]
    },
    {
      category: 'During Your Stay',
      tips: [
        'Always carry your passport or ID',
        'Dress modestly, especially women',
        'Respect prayer times and religious customs',
        'Use official taxis or ride-hailing apps',
        'Keep emergency numbers handy'
      ]
    },
    {
      category: 'Cultural Etiquette',
      tips: [
        'Remove shoes when entering homes',
        'Accept tea when offered (it\'s polite)',
        'Use right hand for eating and handshakes',
        'Don\'t point with index finger',
        'Show respect for religious sites'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
              <FaPlane className="text-4xl" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Travel Guide to Iran</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
            Everything you need to know about airports, transportation, and getting around in Iran as a traveler
          </p>
          <div className="mt-8">
            <button className="bg-white text-blue-700 font-semibold px-8 py-3 rounded-full shadow-lg hover:bg-gray-100 transition flex items-center mx-auto">
              <FaDownload className="mr-2" /> Download Complete Guide
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 gap-2">
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center px-6 py-3 rounded-full font-medium whitespace-nowrap transition ${
                  activeSection === section.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                {section.icon}
                <span className="ml-2">{section.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Airports Section */}
        {activeSection === 'airports' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Airports & Arrival</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Iran has several international airports. Here's what you need to know about the main ones.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {airportInfo.map((airport, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="bg-blue-600 text-white p-4">
                    <h3 className="text-xl font-bold">{airport.name}</h3>
                    <p className="text-blue-100">{airport.location} ({airport.code})</p>
                  </div>
                  <div className="p-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Features:</h4>
                    <ul className="space-y-2 mb-4">
                      {airport.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <FaCheckCircle className="text-green-500 mr-2 text-sm" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <FaInfoCircle className="inline mr-1" />
                        <strong>Tip:</strong> {airport.tips}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-yellow-600 text-xl mr-3 mt-1" />
                <div>
                  <h3 className="font-semibold text-yellow-800 mb-2">Important Arrival Notes</h3>
                  <ul className="text-gray-800 space-y-1">
                    <li>• Have your passport and visa ready</li>
                    <li>• Declare any large amounts of currency</li>
                    <li>• Be prepared for security checks</li>
                    <li>• Have your accommodation address ready</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transportation Section */}
        {activeSection === 'transportation' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Getting Around Iran</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Iran offers various transportation options. Choose the one that best fits your needs and budget.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {transportationOptions.map((option, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center mb-4">
                    <div className="text-3xl mr-4">{option.icon}</div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{option.type}</h3>
                      <p className="text-gray-600">{option.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Available in:</h4>
                      <p className="text-gray-600">{option.cities}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Tips:</h4>
                      <p className="text-gray-600">{option.tips}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Useful Phrases:</h4>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-800 font-medium">{option.phrases[0]}</p>
                        <p className="text-gray-600 text-sm">{option.phrases[1]}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-800 mb-3">Transportation Apps</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2">
                    <FaCar className="text-blue-600 text-2xl" />
                  </div>
                  <h4 className="font-medium text-blue-800">Snapp</h4>
                  <p className="text-sm text-blue-600">Ride-hailing & food delivery</p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2">
                    <FaBus className="text-green-600 text-2xl" />
                  </div>
                  <h4 className="font-medium text-green-800">Tehran Metro</h4>
                  <p className="text-sm text-green-600">Metro schedules & routes</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2">
                    <FaTrain className="text-purple-600 text-2xl" />
                  </div>
                  <h4 className="font-medium text-purple-800">Iran Railways</h4>
                  <p className="text-sm text-purple-600">Train booking & schedules</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Essential Phrases Section */}
        {activeSection === 'phrases' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Essential Persian Phrases</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Learn these key phrases to help you navigate Iran more easily. Practice pronunciation before your trip!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {essentialPhrases.map((phrase, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md p-6">
                  <div className="text-center mb-4">
                    <div className="bg-blue-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3">
                      <span className="text-blue-600 text-xl font-bold">{index + 1}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{phrase.english}</h3>
                    <p className="text-sm text-gray-500">{phrase.context}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-800 mb-1">{phrase.persian}</p>
                      <p className="text-sm text-gray-600">{phrase.transliteration}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="font-semibold text-green-800 mb-3">Pronunciation Tips</h3>
              <ul className="text-green-700 space-y-2">
                <li>• Persian is read right to left</li>
                <li>• Stress is usually on the last syllable</li>
                <li>• "kh" sounds like the "ch" in Scottish "loch"</li>
                <li>• "gh" is a voiced version of "kh"</li>
                <li>• Practice with native speakers when possible</li>
              </ul>
            </div>
          </div>
        )}

        {/* Travel Tips Section */}
        {activeSection === 'tips' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Essential Travel Tips</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Make your trip to Iran smoother with these practical tips and cultural insights.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {travelTips.map((category, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md p-6">
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${
                      index === 0 ? 'bg-blue-100' : index === 1 ? 'bg-green-100' : 'bg-purple-100'
                    }`}>
                      {index === 0 ? (
                        <FaCalendarAlt className="text-2xl text-blue-600" />
                      ) : index === 1 ? (
                        <FaMapMarkerAlt className="text-2xl text-green-600" />
                      ) : (
                        <FaHandshake className="text-2xl text-purple-600" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{category.category}</h3>
                  </div>
                  
                  <ul className="space-y-3">
                    {category.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start">
                        <FaCheckCircle className="text-green-500 mr-3 mt-1 text-sm flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-red-600 text-xl mr-3 mt-1" />
                <div>
                  <h3 className="font-semibold text-red-800 mb-2">Important Reminders</h3>
                  <ul className="text-red-700 space-y-1">
                    <li>• Always carry identification</li>
                    <li>• Respect local customs and dress codes</li>
                    <li>• Be aware of prayer times</li>
                    <li>• Keep emergency contacts handy</li>
                    <li>• Follow local laws and regulations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resources Section */}
        {activeSection === 'resources' && (
          <div className="space-y-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Additional Resources</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Enhance your travel experience with these helpful resources and tools.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <div className="bg-blue-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <FaPhone className="text-blue-600 text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Emergency Numbers</h3>
                <div className="space-y-2 text-left">
                  <p><strong>Police:</strong> 110</p>
                  <p><strong>Ambulance:</strong> 115</p>
                  <p><strong>Fire:</strong> 125</p>
                  <p><strong>Tourist Police:</strong> +98 21 6670 0000</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <div className="bg-green-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <FaGlobe className="text-green-600 text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Useful Websites</h3>
                <div className="space-y-2 text-left">
                  <p><strong>Iran Tourism:</strong> iran.travel</p>
                  <p><strong>Visa Info:</strong> mfa.ir</p>
                  <p><strong>Weather:</strong> irimo.ir</p>
                  <p><strong>Transport:</strong> metro.tehran.ir</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 text-center">
                <div className="bg-purple-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                  <FaDownload className="text-purple-600 text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mobile Apps</h3>
                <div className="space-y-2 text-left">
                  <p><strong>Snapp:</strong> Ride-hailing</p>
                  <p><strong>Google Translate:</strong> Offline Persian</p>
                  <p><strong>Maps.me:</strong> Offline maps</p>
                  <p><strong>SnappFood:</strong> Food delivery</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Explore Iran?</h3>
              <p className="text-lg text-gray-600 mb-6">
                Check out our other travel guides for more specific information about food, accommodation, and cultural experiences.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/food-guide" className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition">
                  Food Guide
                </Link>
                <Link to="/stay-guide" className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition">
                  Accommodation Guide
                </Link>
                <Link to="/culture-guide" className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition">
                  Culture Guide
                </Link>
                <Link to="/travel-companion" className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition">
                  Travel Companion
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Travel Guide to Iran</h3>
              <p className="text-gray-400">Your comprehensive guide to navigating Iran with confidence. From airports to cultural etiquette, we've got you covered.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#airports" className="text-gray-400 hover:text-blue-400 transition">Airports & Arrival</a></li>
                <li><a href="#transportation" className="text-gray-400 hover:text-blue-400 transition">Transportation</a></li>
                <li><a href="#phrases" className="text-gray-400 hover:text-blue-400 transition">Essential Phrases</a></li>
                <li><a href="#tips" className="text-gray-400 hover:text-blue-400 transition">Travel Tips</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Other Guides</h4>
              <ul className="space-y-2">
                <li><Link to="/food-guide" className="text-gray-400 hover:text-blue-400 transition">Food Guide</Link></li>
                <li><Link to="/stay-guide" className="text-gray-400 hover:text-blue-400 transition">Accommodation Guide</Link></li>
                <li><Link to="/culture-guide" className="text-gray-400 hover:text-blue-400 transition">Culture Guide</Link></li>
                <li><Link to="/travel-guide" className="text-gray-400 hover:text-blue-400 transition">Travel Guide</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact & Support</h4>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-400"><FaPhone className="mr-2" /> +98 21 2345 6789</li>
                <li className="flex items-center text-gray-400"><FaGlobe className="mr-2" /> support@Pirooz.com</li>
                <li className="flex items-center text-gray-400"><FaInfoCircle className="mr-2" /> Emergency: 110</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">© 2024 پیروز Pirooz. All rights reserved.</p>
            <div className="flex space-x-6">
              <a href="https://instagram.com/Piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition"><FaInstagram className="text-xl" /></a>
              <a href="https://t.me/Piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition"><FaTelegram className="text-xl" /></a>
              <a href="https://wa.me/Piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition"><FaWhatsapp className="text-xl" /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TravelGuide; 